'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import { Turnstile } from 'next-turnstile';
import { useEffect } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import axios from 'axios';
import { toast } from 'sonner';
import { z } from 'zod';
import { Field, FieldError, FieldGroup, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Content, Markdown } from '@/lib/markdown';

interface ContactProps {
	title?: string;
	description?: Content | string;
	hideTitle?: boolean;
	className?: string;
}

const FormSchema = z.object({
	fullName: z.string().min(1, 'Full name is required'),
	phone: z.string().min(1, 'Phone is required'),
	email: z.string().email('Invalid email address'),
	companyName: z.string().min(1, 'Company name is required'),
	message: z.string().min(1, 'Message is required'),
	'cf-turnstile-response': z.string().min(1, 'Please verify you are human'),
	sourcePage: z.string().optional(),
});

export function Contact(props: ContactProps) {
	const form = useForm<z.infer<typeof FormSchema>>({
		resolver: zodResolver(FormSchema),
		defaultValues: {
			fullName: '',
			phone: '',
			email: '',
			companyName: '',
			message: '',
			'cf-turnstile-response': '',
			sourcePage: '',
		},
	});

	useEffect(() => {
		if (typeof window !== 'undefined') {
			form.setValue('sourcePage', window.location.pathname);
		}
	}, [form]);

	const onSubmit: SubmitHandler<z.infer<typeof FormSchema>> = async (data) => {
		const sourcePage = typeof window !== 'undefined' ? window.location.pathname : '';
		const formboldId = process.env.NEXT_PUBLIC_FORMBOLD_ID;

		if (!formboldId) {
			toast.error('Form configuration error. Please contact support.');
			console.error('NEXT_PUBLIC_FORMBOLD_ID is not defined.');
			return;
		}

		try {
			// Exclude cf-turnstile-response from formbold submission as it's not verified on server anymore
			// in this static export configuration.
			const { 'cf-turnstile-response': _, ...formboldData } = { ...data, sourcePage };

			await axios({
				method: 'POST',
				url: `https://formbold.com/s/${formboldId}`,
				data: formboldData,
			});

			toast.success('Message sent successfully');
			form.reset();
		} catch (error) {
			console.error(error, 'Contact form submission failed');
			toast.error('An error occurred while sending your message. Please try again.');
		}
	};

	return (
		<section className={`flex flex-col gap-4 relative ${props.className}`}>
			<div className="flex flex-col justify-center z-10 relative">
				{!props.hideTitle && (
					<p className="text-2xl font-bold text-gray-900 mb-4">
						{props.title ?? 'Contact Us'}
					</p>
				)}
				{props.description && typeof props.description === 'string' ? (
					<p className="max-w-[68ch]"> {props.description}</p>
				) : props.description ? (
					<Markdown
						className=" text-base [&_p]:lg:text-2xl [&_p]:font-light mb-10"
						content={props.description as Content}
					/>
				) : null}
			</div>
			<div className="flex flex-col rounded-lg z-10 relative">
				<form className="flex flex-col" onSubmit={form.handleSubmit(onSubmit)}>
					<FieldGroup className="gap-4">
						<div className="flex-1">
							<Controller
								name="fullName"
								control={form.control}
								render={({ field, fieldState }) => (
									<Field data-invalid={fieldState.invalid} className="gap-1">
										<FieldLabel
											htmlFor="form-rhf-demo-title"
											className="text-gray-700 font-normal text-sm"
										>
											Full name <span className="text-red-500">*</span>
										</FieldLabel>
										<Input
											{...field}
											id="fullName"
											placeholder="John Doe"
											aria-invalid={fieldState.invalid}
											className="px-5"
										/>
										{fieldState.invalid && (
											<FieldError errors={[fieldState.error]} />
										)}
									</Field>
								)}
							/>
						</div>

						<div className="flex-1">
							<Controller
								name="phone"
								control={form.control}
								render={({ field, fieldState }) => (
									<Field data-invalid={fieldState.invalid} className="gap-1">
										<FieldLabel
											htmlFor="form-rhf-demo-title"
											className="text-gray-700 font-normal text-sm"
										>
											Phone <span className="text-red-500">*</span>
										</FieldLabel>
										<Input
											{...field}
											id="phone"
											placeholder="e.g. (216) 555-2368"
											aria-invalid={fieldState.invalid}
											className="px-5"
										/>
										{fieldState.invalid && (
											<FieldError errors={[fieldState.error]} />
										)}
									</Field>
								)}
							/>
						</div>

						<div className="flex-1">
							<Controller
								name="email"
								control={form.control}
								render={({ field, fieldState }) => (
									<Field data-invalid={fieldState.invalid} className="gap-1">
										<FieldLabel
											htmlFor="form-rhf-demo-title"
											className="text-gray-700 font-normal text-sm"
										>
											Email <span className="text-red-500">*</span>
										</FieldLabel>
										<Input
											{...field}
											id="email"
											placeholder="johndoe@company.com"
											aria-invalid={fieldState.invalid}
											className="px-5"
										/>
										{fieldState.invalid && (
											<FieldError errors={[fieldState.error]} />
										)}
									</Field>
								)}
							/>
						</div>

						<div className="flex-1">
							<Controller
								name="companyName"
								control={form.control}
								render={({ field, fieldState }) => (
									<Field data-invalid={fieldState.invalid} className="gap-1">
										<FieldLabel
											htmlFor="form-rhf-demo-title"
											className="text-gray-700 font-normal text-sm"
										>
											Company name <span className="text-red-500">*</span>
										</FieldLabel>
										<Input
											{...field}
											id="companyName"
											aria-invalid={fieldState.invalid}
											className="px-5"
										/>
										{fieldState.invalid && (
											<FieldError errors={[fieldState.error]} />
										)}
									</Field>
								)}
							/>
						</div>

						<div className="w-full">
							<Controller
								name="message"
								control={form.control}
								render={({ field, fieldState }) => (
									<Field data-invalid={fieldState.invalid} className="gap-1">
										<FieldLabel
											htmlFor="form-rhf-demo-title"
											className="text-gray-700 font-normal text-sm"
										>
											Message
										</FieldLabel>
										<Textarea
											{...field}
											id="message"
											aria-invalid={fieldState.invalid}
											rows={4}
											placeholder="What can we help you with?"
										/>
										{fieldState.invalid && (
											<FieldError errors={[fieldState.error]} />
										)}
									</Field>
								)}
							/>
						</div>

						<div className="flex flex-col gap-4">
							<div data-invalid={form.formState.errors['cf-turnstile-response']}>
								<Turnstile
									siteKey={process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY || ''}
									onVerify={(token) => {
										form.setValue('cf-turnstile-response', token);
									}}
								/>
								{form.formState.errors['cf-turnstile-response'] && (
									<FieldError
										errors={[form.formState.errors['cf-turnstile-response']]}
									/>
								)}
							</div>
							<button
								className="w-full h-fit bg-primary-600 font-semibold text-white py-3 px-6 rounded-sm disabled:opacity-60"
								type="submit"
								disabled={form.formState.isSubmitting}
							>
								{form.formState.isSubmitting ? 'Submitting…' : 'Submit'}
							</button>
						</div>
					</FieldGroup>
				</form>
			</div>
		</section>
	);
}
